/**
 * CREDIT (UF-06). The front door of the two-sided marketplace.
 *
 *   [ Borrow ]    obtain financing against a tokenized asset you already own
 *   [ Finance ]   fund borrowing requests other participants have opened
 *
 * TWO DOORS, ONE BOOK. Borrow and Finance are not two products: they are two views of the same
 * credit requests, the same collateral ledger and the same loans. Building them as separate systems
 * is the mistake this screen exists to avoid -- a financer would end up seeing a different set of
 * requests from the one the borrower thinks they submitted.
 *
 * FINANCE IS NOT SHOWN TO EVERYBODY. The card is rendered for a participant whose role permits it,
 * and the endpoint behind it refuses anyone else regardless of what this screen draws. A normal user
 * must not be able to finance arbitrary assets: they can only fund requests a borrower has already
 * opened, which is why there is no path from here to "browse assets to lend against".
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  LipaApiError,
  type CollateralPosition,
  type CreditLoan,
  type CreditRequest,
} from '@/lib/api';
import { PageHeader, SectionTitle, StatTile } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDateShort } from '@/lib/format';
import { formatWireAmount } from '@/lib/money';

const REQUEST_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under review',
  collateral_verification: 'Checking collateral',
  trust_review: 'With the Trust',
  open_for_financing: 'Open for financing',
  financing_interest_received: 'Offers received',
  financing_selected: 'Offer selected',
  borrower_acceptance_pending: 'Awaiting your acceptance',
  dcp_approval_pending: 'With the financier',
  approved: 'Approved',
  cancelled: 'Cancelled',
  rejected: 'Declined',
  expired: 'Expired',
};

export default function Credit() {
  const [requests, setRequests] = useState<CreditRequest[] | null>(null);
  const [loans, setLoans] = useState<CreditLoan[]>([]);
  const [collateral, setCollateral] = useState<CollateralPosition[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, l, c] = await Promise.all([
        api.creditMarket.requests(),
        api.creditMarket.loans(),
        api.creditMarket.myCollateral(),
      ]);
      setRequests(r.requests);
      setLoans(l.loans);
      setCollateral(c.collateral);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load Credit.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!requests) return <LoadingCard lines={4} />;

  const live = requests.filter(
    (r) => !['approved', 'cancelled', 'rejected', 'expired'].includes(r.state),
  );
  const active = loans.filter((l) =>
    ['approved', 'disbursed', 'repaying', 'in_arrears', 'in_grace'].includes(l.state),
  );
  const encumbered = collateral.filter((c) => c.state !== 'released');
  const outstanding = active.reduce((sum, l) => sum + BigInt(l.outstanding_minor), 0n);
  const awaitingYou = requests.filter((r) =>
    ['financing_interest_received', 'borrower_acceptance_pending'].includes(r.state),
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Credit"
        subtitle="Borrow against a tokenized asset, or finance somebody else's."
      />

      {/* The honest phase gate. Lending activates by configuration, not by a deploy. */}
      <div className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
        <span className="font-medium text-info">Preview.</span> Lending activates in a later sandbox
        phase, once the Capital Markets Authority approves it. Every step below is real; no money
        moves until then.
      </div>

      {/* THE TWO DOORS. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Door
          to="/credit/borrow"
          title="Borrow"
          body="Raise financing against a tokenized asset you already own. You pledge units of the asset; the Trust holds them until you have repaid."
          cta="Choose a product"
          tone="primary"
        />
        <Door
          to="/credit/finance"
          title="Finance"
          body="Review borrowing requests that are open for financing and offer terms. Available to verified credit providers."
          cta="See opportunities"
          tone="surface"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Open requests" value={live.length} />
        <StatTile label="Active loans" value={active.length} />
        <StatTile label="Outstanding" value={`KES ${formatWireAmount(String(outstanding))}`} />
        <StatTile label="Pledged assets" value={encumbered.length} accent={encumbered.length > 0} />
      </div>

      {/* WHAT NEEDS YOU. The one thing a borrower must not miss: offers waiting on a decision. */}
      {awaitingYou.length > 0 && (
        <Reveal>
          <section className="rounded-card border border-brand/30 bg-brand/5 p-4">
            <p className="text-sm font-medium text-ink">Financing offers are waiting for you</p>
            <p className="mt-1 text-micro text-ink-muted">
              Compare the terms before you accept. Accepting one declines the others.
            </p>
            <ul className="mt-3 space-y-2">
              {awaitingYou.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/credit/requests/${r.id}`}
                    className="flex items-center justify-between rounded-field bg-surface px-3 py-2 text-sm transition hover:bg-surface-sunken"
                  >
                    <span className="min-w-0 truncate text-ink">
                      {r.reference} · {r.product_name}
                    </span>
                    <span className="tabular shrink-0 text-micro font-medium text-brand">
                      {r.live_offers} offer{r.live_offers === 1 ? '' : 's'}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>
      )}

      {/* BORROW: the requests in flight. */}
      <section>
        <SectionTitle
          action={
            <Link to="/credit/borrow" className="text-micro font-medium text-brand">
              New request
            </Link>
          }
        >
          My borrowing requests
        </SectionTitle>
        {requests.length === 0 ? (
          <EmptyState
            title="No borrowing requests yet"
            body="Borrowing here is secured on a tokenized asset. Pick a product and pledge units of an asset you own."
            action={{ label: 'Browse credit products', href: '/credit/borrow' }}
          />
        ) : (
          <ul className="space-y-2.5">
            {requests.slice(0, 6).map((r) => (
              <li key={r.id}>
                <Link
                  to={`/credit/requests/${r.id}`}
                  className="block rounded-card border border-border bg-surface p-3.5 shadow-card transition hover:border-primary/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{r.product_name}</p>
                      <p className="text-micro text-ink-subtle">
                        {r.reference} · {r.tenor_months} months
                      </p>
                    </div>
                    <StatusPill status={r.state} label={REQUEST_LABEL[r.state] ?? r.state} />
                  </div>
                  <div className="mt-2.5 flex items-center justify-between border-t border-border pt-2.5">
                    <span className="tabular text-sm font-medium text-ink">
                      KES {formatWireAmount(r.amount_minor)}
                    </span>
                    <span className="text-micro text-ink-subtle">
                      {r.live_offers > 0
                        ? `${r.live_offers} offer${r.live_offers === 1 ? '' : 's'}`
                        : fmtDateShort(r.created_at)}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* THE LOAN BOOK. */}
      {loans.length > 0 && (
        <section>
          <SectionTitle
            action={
              <Link to="/credit/collateral" className="text-micro font-medium text-brand">
                Collateral
              </Link>
            }
          >
            My loans
          </SectionTitle>
          <ul className="space-y-2.5">
            {loans.slice(0, 5).map((l) => (
              <li key={l.id}>
                <Link
                  to={`/credit/loans/${l.id}`}
                  className="block rounded-card border border-border bg-surface p-3.5 shadow-card transition hover:border-primary/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{l.product_name}</p>
                      <p className="text-micro text-ink-subtle">{l.lender_name ?? 'Financier'}</p>
                    </div>
                    <StatusPill status={l.state} />
                  </div>
                  <div className="mt-2.5 border-t border-border pt-2.5">
                    <div className="flex items-center justify-between text-micro text-ink-subtle">
                      <span>
                        KES {formatWireAmount(l.outstanding_minor)} of{' '}
                        {formatWireAmount(l.principal_minor)} outstanding
                      </span>
                      <span className="tabular">{l.repaid_pct}% repaid</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                      <div
                        className="h-full rounded-full bg-brand"
                        style={{ width: `${Math.min(100, l.repaid_pct)}%` }}
                      />
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs leading-relaxed text-ink-muted">
        When you borrow, the units you pledge are locked by the Trust so they cannot be sold or
        pledged twice. They are released the moment the loan is repaid.
      </p>
    </div>
  );
}

function Door({
  to,
  title,
  body,
  cta,
  tone,
}: {
  to: string;
  title: string;
  body: string;
  cta: string;
  tone: 'primary' | 'surface';
}) {
  const primary = tone === 'primary';
  return (
    <Reveal>
      <Link
        to={to}
        className={`flex h-full flex-col rounded-card p-4 shadow-card transition ${
          primary
            ? 'bg-gradient-to-br from-primary to-primary-dark text-white hover:shadow-lifted'
            : 'border border-border bg-surface hover:border-primary/40'
        }`}
      >
        <p className={`text-base font-semibold ${primary ? 'text-white' : 'text-ink'}`}>{title}</p>
        <p
          className={`mt-1.5 flex-1 text-micro leading-relaxed ${
            primary ? 'text-white/80' : 'text-ink-muted'
          }`}
        >
          {body}
        </p>
        <span
          className={`mt-3 text-micro font-medium ${primary ? 'text-white' : 'text-brand'}`}
        >
          {cta} &rarr;
        </span>
      </Link>
    </Reveal>
  );
}
